import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, Phone, Mail, MapPin } from "lucide-react";
import { mapsUrl } from "@/lib/maps";
import { api } from "@/lib/api";

const ICONS = { Phone, Email: Mail, Address: MapPin };

export default function About() {
  const { data: about } = useQuery({ queryKey: ["about"], queryFn: () => api.get("/about").then((r) => r.data) });
  const testimonials = about?.testimonials || [];
  const rows = about?.contact_rows || [];

  return (
    <div className="pt-28 pb-24 max-w-5xl mx-auto px-5" data-testid="about-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Who We Are</p>
      <h1 className="font-display text-5xl md:text-6xl text-white mt-3 mb-8">The Studio</h1>

      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-white/70 text-lg leading-relaxed whitespace-pre-line max-w-3xl">
        {about?.story}
      </motion.p>

      {testimonials.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-8">What Clients Say</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#111] border border-white/10 rounded-2xl p-6"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="text-[#D4AF37] mb-3" size={24} />
                <p className="text-white/75 text-sm leading-relaxed italic">"{t.quote}"</p>
                <div className="mt-4">
                  <p className="text-white font-display">{t.name}</p>
                  <p className="text-white/40 text-xs">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-20 glass rounded-2xl p-8">
        <h2 className="font-display text-2xl text-white mb-6">Get In Touch</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {rows.map((r, i) => {
            const Icon = ICONS[r.label] || Phone;
            return (
              <div key={i} className="flex items-start gap-3" data-testid={`contact-row-${i}`}>
                <Icon className="text-[#D4AF37] mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wide font-mono-label">{r.label}</p>
                  {r.label === "Address" ? (
                    <a href={mapsUrl(r.value)} target="_blank" rel="noreferrer" className="text-white text-sm mt-0.5 block hover:text-[#D4AF37] underline-offset-4 hover:underline" data-testid="about-address-link">{r.value}</a>
                  ) : r.label.startsWith("Phone") ? (
                    <a href={`tel:${r.value.replace(/[^\d+]/g, "")}`} className="text-white text-sm mt-0.5 block hover:text-[#D4AF37]">{r.value}</a>
                  ) : r.label === "Email" ? (
                    <a href={`mailto:${r.value}`} className="text-white text-sm mt-0.5 block hover:text-[#D4AF37]">{r.value}</a>
                  ) : <p className="text-white text-sm mt-0.5">{r.value}</p>}
                </div>
              </div>
            );
          })}
        </div>
        <Link to="/book" data-testid="about-book-btn" className="btn-gold inline-flex rounded-full px-8 py-3 mt-8 uppercase tracking-wide font-mono-label text-sm">
          Book a Session
        </Link>
      </div>
    </div>
  );
}
